import { formatDate as localeDate } from 'i18n/format';
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import {
  usePipeline,
  usePipelineRuns,
  usePipelineHealth,
  useDeletePipeline,
  useTriggerPipeline,
  useTogglePipeline,
  useUpdatePipeline,
  useBackfillPipeline,
} from 'api/pipelines';
import { extractApiError, hasFieldErrors, normalizeFieldErrors } from 'api/errors';
import ScheduleCronFields, {
  CUSTOM_PRESET,
  presetForCron,
} from 'components/pipelines/ScheduleCronFields';
import { nextRunText, scheduleText } from 'components/pipelines/schedule';
import { failureOf, supportHref, triggerText } from 'components/pipelines/loadStatus';
import Section, { Row } from 'components/pipelines/Section';
import Verdict from 'components/pipelines/Verdict';
import WhatItFeeds from 'components/pipelines/WhatItFeeds';
import HealthStrip, { StripLegend } from 'components/pipelines/HealthStrip';
import RunRef from 'components/pipelines/RunRef';
import useRole from 'hooks/useRole';
import AsyncTaskButton from 'components/common/AsyncTaskButton';
import { useStopFlow } from 'api/flows';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import { useTokens } from 'theme';
import { durationLabel, plural, rowsLabel, whenInline, whenLabel } from 'utils/ru';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

const HISTORY_PAGE = 15;
const TABLES_SHOWN = 5;

const runTime = (run) => run.started_at || run.created_at;
const isActive = (run) => run?.status === 'running' || run?.status === 'pending';

/** The source's own page, where access and settings are fixed. */
const sourcePath = (pipeline) =>
  pipeline.source_connection?.public_id ? `/console/connectors/${pipeline.source_connection.public_id}` : null;

/**
 * The phrase at the top: how the data is doing, from the pipeline's flags,
 * its latest forward run and the backend's health summary (for "late").
 */
function headline(pipeline, lastOk, health, now) {
  const last = pipeline.last_run;
  const lastDataAt = health?.last_data_at || (lastOk && (lastOk.finished_at || runTime(lastOk)));
  const lastData = lastDataAt ? t('data.latestDataArrivedValue', { value1: whenInline(lastDataAt, now) }) : null;
  const next = nextRunText(pipeline.schedule_cron, now, pipeline.schedule_timezone);

  if (pipeline.paused_by_connection) {
    return { tone: 'bad', title: t('data.pipelinePausedTheSourceAccessCheckFailed'), text: lastData, fix: 'source' };
  }
  if (pipeline.source_connection_status === 'error') {
    return { tone: 'bad', title: t('data.theSourceDeniedAccess'), text: lastData, fix: 'source' };
  }
  if (pipeline.paused_by_billing) {
    return { tone: 'neutral', title: t('data.pipelineStoppedSubscriptionSuspended'), text: lastData };
  }
  if (!pipeline.is_active) {
    return { tone: 'neutral', title: t('data.pipelineDisabled'), text: lastData, fix: 'enable' };
  }
  if (isActive(last)) {
    return {
      tone: 'info',
      title: last.status === 'running' ? t('data.loadingData') : t('data.pipelineQueued'),
      text: t('data.startedValueValue', { value1: whenInline(runTime(last), now), value2: triggerText(last) }),
    };
  }
  if (last?.status === 'failed') {
    const f = failureOf(last);
    return { tone: 'bad', title: t('data.theLastPipelineRunFailedValue', { value1: f.text.toLowerCase() }), text: lastData, fix: f.fix, run: last };
  }
  if (health?.state === 'late') {
    return {
      tone: 'warn',
      title: t('data.dataIsLate'),
      text: t('data.expectedValue', { value1: whenInline(health.late_since, now) }),
    };
  }
  if (!lastOk) {
    return {
      tone: 'neutral',
      title: t('data.noPipelineRunsYet'),
      text: next ? t('data.firstRunValue', { value1: next }) : t('data.startTheFirstRunManually'),
    };
  }
  const nextLine = next ? t('data.nextRunValue', { value1: next }) : '';
  if (!pipeline.schedule_cron) {
    return {
      tone: 'neutral',
      title: t('data.loadedManuallyValue', { value1: whenInline(lastOk.finished_at || runTime(lastOk), now) }),
      text: t('data.noScheduleDataUpdatesWhenYouStartARun'),
    };
  }
  if (lastOk.row_count === 0) {
    return { tone: 'ok', title: t('data.runSucceededWithNoNewData'), text: `${lastData}${nextLine}` };
  }
  const rows = lastOk.row_count != null ? t('data.valueArrived', { value1: rowsLabel(lastOk.row_count) }) : t('data.dataArrived');
  return {
    tone: 'ok',
    title: t('data.dataIsFresh'),
    text: `${whenLabel(lastOk.finished_at || runTime(lastOk), now)}${rows}.${nextLine}`,
  };
}

export default function PipelineDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const themeTokens = useTokens();

  const { data: pipeline, isLoading, error } = usePipeline(id);
  const [historyShown, setHistoryShown] = useState(HISTORY_PAGE);
  const [tablesOpen, setTablesOpen] = useState(false);
  const { data: runsPage, isLoading: runsLoading } = usePipelineRuns(id, historyShown);
  const runs = runsPage?.results;
  const runsTotal = runsPage?.count || 0;
  const { data: health } = usePipelineHealth(id);
  const deletePipeline = useDeletePipeline();
  const triggerPipeline = useTriggerPipeline(id);
  const togglePipeline = useTogglePipeline(id);
  const backfillPipeline = useBackfillPipeline(id);
  const updatePipeline = useUpdatePipeline(id);
  const stopFlow = useStopFlow(id);

  const { isAdmin } = useRole();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showBackfill, setShowBackfill] = useState(false);
  const [bfStart, setBfStart] = useState('');
  const [bfEnd, setBfEnd] = useState('');
  const [bfSliceDays, setBfSliceDays] = useState('');
  const [bfParallelism, setBfParallelism] = useState('');
  const [bfFieldErrors, setBfFieldErrors] = useState({});
  const [backfillError, setBackfillError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [showScheduleEdit, setShowScheduleEdit] = useState(false);
  const [schedPreset, setSchedPreset] = useState('');
  const [schedCustomCron, setSchedCustomCron] = useState('');
  const [schedCronError, setSchedCronError] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);

  // The runs list mixes forward runs and backfill slices; either in progress
  // blocks a new manual run, mirroring the backend one-active-run guard.
  const anyRunInProgress = !!runs?.some(isActive);
  // The backend's stop excludes backfill slices, so offer it only while the
  // latest forward run is active.
  const forwardRuns = (runs || []).filter((r) => !r.backfill_job);
  const forwardRunActive = isActive(forwardRuns[0]);
  const lastOk = forwardRuns.find((r) => r.status === 'success') || null;

  const closeMenu = () => setMenuAnchor(null);

  const handleStop = async () => {
    setActionError(null);
    try {
      await stopFlow.mutateAsync();
    } catch (err) {
      setActionError(extractApiError(err, t('data.couldNotStopThePipelinePleaseTryAgain')));
    }
  };

  const handleToggle = async () => {
    closeMenu();
    setActionError(null);
    try {
      await togglePipeline.mutateAsync();
    } catch (err) {
      setActionError(extractApiError(err, t('data.couldNotToggleThePipeline')));
    }
  };

  const confirmDelete = async () => {
    setDeleteError(null);
    try {
      await deletePipeline.mutateAsync(pipeline.public_id);
      navigate('/console/pipelines');
    } catch (err) {
      setDeleteError(extractApiError(err, t('data.couldNotDeleteThePipelinePleaseTryAgain')));
    }
  };

  const openScheduleDialog = () => {
    closeMenu();
    const preset = presetForCron(pipeline.schedule_cron);
    setSchedPreset(preset);
    setSchedCustomCron(preset === CUSTOM_PRESET ? pipeline.schedule_cron : '');
    setSchedCronError(null);
    setScheduleError(null);
    setShowScheduleEdit(true);
  };

  const handleScheduleSave = async () => {
    const cron = schedPreset === CUSTOM_PRESET ? schedCustomCron.trim() : schedPreset;
    if (schedPreset === CUSTOM_PRESET && !cron) {
      setSchedCronError(t('data.enterACronExpressionOrChooseManualOnly'));
      return;
    }
    setSchedCronError(null);
    setScheduleError(null);
    try {
      await updatePipeline.mutateAsync({ schedule_cron: cron });
      setShowScheduleEdit(false);
    } catch (err) {
      const fields = hasFieldErrors(err) ? normalizeFieldErrors(err.response.data) : {};
      // The inline cron field only exists in custom mode; in any other mode the
      // message has to land on the dialog-level alert to be seen at all.
      if (fields.schedule_cron && schedPreset === CUSTOM_PRESET) {
        setSchedCronError(fields.schedule_cron);
      } else {
        setScheduleError(fields.schedule_cron || extractApiError(err, t('data.couldNotSaveTheSchedule')));
      }
    }
  };

  const openBackfillDialog = () => {
    closeMenu();
    setBfStart('');
    setBfEnd('');
    setBfSliceDays('');
    setBfParallelism('');
    setBfFieldErrors({});
    setBackfillError(null);
    setShowBackfill(true);
  };

  const handleBackfillSubmit = async () => {
    const errors = {};
    if (!bfStart) errors.start_date = t('data.enterAStartDate');
    if (!bfEnd) errors.end_date = t('data.enterAnEndDate');
    if (bfStart && bfEnd && bfStart >= bfEnd) {
      errors.end_date = t('data.theEndDateMustBeAfterTheStartDate');
    }
    setBfFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setBackfillError(null);

    const payload = { start_date: bfStart, end_date: bfEnd };
    if (bfSliceDays) payload.slice_days = Number(bfSliceDays);
    if (bfParallelism) payload.parallelism = Number(bfParallelism);

    try {
      await backfillPipeline.mutateAsync(payload);
      setShowBackfill(false);
    } catch (err) {
      setBackfillError(extractApiError(err, t('data.couldNotStartTheBackfillPleaseTryAgain')));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pipeline) {
    return (
      <Alert severity="error">
        {extractApiError(error, t('data.couldNotLoadThePipelinePage'))}{' '}
        <Button size="small" onClick={() => navigate('/console/pipelines')}>{t('data.backToList')}</Button>
      </Alert>
    );
  }

  const now = new Date();
  const head = headline(pipeline, lastOk, health, now);
  const source = pipeline.source_connection;
  const destination = pipeline.warehouse?.name || pipeline.destination_connection?.name;

  const fixAction = (fix, run) => {
    if (fix === 'source' && sourcePath(pipeline)) {
      return (
        <Button variant="contained" size="small" component={RouterLink} to={sourcePath(pipeline)}>{t('data.openSource')}</Button>
      );
    }
    if (fix === 'support') {
      return (
        <Button variant="outlined" size="small" href={supportHref(pipeline.name, run)}>{t('data.contactSupport')}</Button>
      );
    }
    if (fix === 'enable') {
      return (
        <Button variant="contained" size="small" onClick={handleToggle} disabled={togglePipeline.isPending}>{t('data.enablePipeline')}</Button>
      );
    }
    return null;
  };

  const tables = Array.isArray(lastOk?.load_summary?.tables) ? lastOk.load_summary.tables : [];
  const nextText = nextRunText(pipeline.schedule_cron, now, pipeline.schedule_timezone);
  // A paused load keeps its cron, but the scheduler will not start it.
  const runsOnSchedule = pipeline.is_active && !pipeline.paused_by_connection && !pipeline.paused_by_billing;

  // A failure is "current" until a later forward run succeeds; earlier ones
  // are history and go grey, without an action.
  const lastOkTime = lastOk ? new Date(runTime(lastOk)).getTime() : 0;
  const history = runs || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/console/pipelines')}
          size="small"
          color="inherit"
          sx={{ ml: -1 }}
        >{t('data.pipelines3')}</Button>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700}>
            {pipeline.name}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: themeTokens.inkMuted, mt: 0.25 }}>{t('data.source2')}{' '}
            {sourcePath(pipeline) ? (
              <Link component={RouterLink} to={sourcePath(pipeline)} underline="hover" sx={{ fontWeight: 700 }}>
                {source.name}
              </Link>
            ) : (
              source?.name || '—'
            )}
            {destination ? t('data.toWarehouseValue', { value1: destination }) : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isAdmin && forwardRunActive && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={<StopIcon />}
              disabled={stopFlow.isPending}
              onClick={handleStop}
            >{t('data.stop')}</Button>
          )}
          <AsyncTaskButton
            label={t('data.runNow')}
            loadingLabel={t('data.starting')}
            onTrigger={() => triggerPipeline.mutateAsync()}
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            disabled={!pipeline.is_active || anyRunInProgress}
          />
          <IconButton
            size="small"
            aria-label={t('data.moreActions')}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ border: `1px solid ${themeTokens.borderField}`, borderRadius: '8px' }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {isAdmin && <MenuItem onClick={openScheduleDialog}>{t('data.editSchedule')}</MenuItem>}
            {isAdmin && pipeline.backfill_available && (
              <MenuItem onClick={openBackfillDialog}>{t('data.loadHistory')}</MenuItem>
            )}
            <MenuItem onClick={handleToggle} disabled={togglePipeline.isPending}>
              {pipeline.is_active ? t('data.disablePipeline') : t('data.enablePipeline')}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => { closeMenu(); setDeleteError(null); setShowDelete(true); }}
              sx={{ color: themeTokens.negative }}
            >{t('data.delete2')}</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Verdict tone={head.tone} title={head.title} text={head.text} action={fixAction(head.fix, head.run)} />

      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '340px minmax(0,1fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Section title={t('data.schedule')}>
            <Row label={t('data.when')}>{scheduleText(pipeline.schedule_cron, now, pipeline.schedule_timezone)}</Row>
            {nextText && runsOnSchedule && <Row label={t('data.next2')}>{nextText}</Row>}
          </Section>

          <Section
            title={t('data.dataReceived')}
            aside={tables.length ? `${tables.length} ${plural(tables.length, [t('data.table'), t('data.tables2'), t('data.tables3')])}` : null}
          >
            {!lastOk ? (
              <Typography sx={{ fontSize: '0.85rem', color: themeTokens.inkMuted }}>{t('data.nothingYet')}</Typography>
            ) : tables.length ? (
              <>
                {(tablesOpen ? tables : tables.slice(0, TABLES_SHOWN)).map((table) => (
                  <Box key={`${table.namespace}.${table.table}`} sx={{ py: 0.75, borderTop: `1px solid ${themeTokens.border}`, '&:first-of-type': { borderTop: 'none', pt: 0 } }}>
                    <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', fontWeight: 600 }}>
                      {table.table}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted }}>
                      {whenInline(lastOk.finished_at || runTime(lastOk), now)}
                      {typeof table.rows === 'number' ? ` · ${rowsLabel(table.rows)}` : ''}
                    </Typography>
                  </Box>
                ))}
                {tables.length > TABLES_SHOWN && (
                  <Button size="small" sx={{ mt: 0.5, ml: -0.75 }} onClick={() => setTablesOpen((v) => !v)}>
                    {tablesOpen ? t('data.collapse') : t('data.showAllValue', { value1: tables.length })}
                  </Button>
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: '0.85rem' }}>
                {whenLabel(lastOk.finished_at || runTime(lastOk), now)}
                {lastOk.row_count != null ? ` · ${rowsLabel(lastOk.row_count)}` : ''}
              </Typography>
            )}
          </Section>

          <WhatItFeeds pipeline={pipeline} />
        </Box>

        <Section title={t('data.history')} aside={runsTotal ? `${runsTotal} ${plural(runsTotal, [t('data.run'), t('data.runs'), t('data.runs2')])}` : null}>
          {health && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                gap: 1.5,
                pb: 1.5,
                mb: 1,
                borderBottom: `1px solid ${themeTokens.border}`,
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', color: themeTokens.inkMuted }}>{t('data.last14')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                <HealthStrip health={health} size="large" />
                <StripLegend />
              </Box>
            </Box>
          )}
          {runsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !runs?.length ? (
            <Typography sx={{ fontSize: '0.85rem', color: themeTokens.inkMuted }}>{t('data.noRunsYetClickRunNowToLoadDataForThe')}</Typography>
          ) : (
            <>
              {history.map((run) => (
                <HistoryRow
                  key={run.public_id}
                  run={run}
                  now={now}
                  past={run.status === 'failed' && new Date(runTime(run)).getTime() < lastOkTime}
                  action={
                    run.status === 'failed' && run.public_id === pipeline.last_run?.public_id
                      ? fixAction(failureOf(run).fix, run)
                      : null
                  }
                />
              ))}
              {runsTotal > history.length && (
                <Button size="small" sx={{ mt: 1 }} onClick={() => setHistoryShown((n) => n + HISTORY_PAGE)}>{t('data.showMore')}</Button>
              )}
            </>
          )}
        </Section>
      </Box>

      <ConfirmActionDialog
        open={showDelete}
        title={t('data.deletePipeline')}
        confirmWord={t('data.delete')}
        confirmLabel={t('data.deletePipeline')}
        confirmColor="error"
        loading={deletePipeline.isPending}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setShowDelete(false)}
      >
        <Typography variant="body2">{t('data.pipeline2')}{' '}<strong>{pipeline.name}</strong>{' '}{t('data.willBeDeletedTogetherWithItsScheduleAndRunHistoryThis')}</Typography>
      </ConfirmActionDialog>

      {/* Edit schedule */}
      <Dialog
        open={showScheduleEdit}
        onClose={updatePipeline.isPending ? undefined : () => setShowScheduleEdit(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('data.schedule')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.chooseAPresetOrEnterACustomCronExpressionChangesApply')}</Typography>
          {scheduleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {scheduleError}
            </Alert>
          )}
          <Box sx={{ mt: 0.5 }}>
            <ScheduleCronFields
              zone={pipeline.schedule_timezone}
              preset={schedPreset}
              customCron={schedCustomCron}
              onPresetChange={(v) => {
                setSchedPreset(v);
                setSchedCronError(null);
                setScheduleError(null);
              }}
              onCustomCronChange={(v) => {
                setSchedCustomCron(v);
                setSchedCronError(null);
              }}
              cronError={schedCronError}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowScheduleEdit(false)} disabled={updatePipeline.isPending} color="inherit">{t('data.cancel')}</Button>
          <Button
            onClick={handleScheduleSave}
            variant="contained"
            disabled={updatePipeline.isPending}
            startIcon={updatePipeline.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >{t('data.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Backfill */}
      <Dialog
        open={showBackfill}
        onClose={backfillPipeline.isPending ? undefined : () => setShowBackfill(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('data.loadHistory')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.thePeriodIsSplitIntoChunksLoadedInParallelLimitsAre')}</Typography>
          {backfillError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {backfillError}
            </Alert>
          )}
          <TextField
            label={t('data.fromDate')}
            type="date"
            value={bfStart}
            onChange={(e) => setBfStart(e.target.value)}
            required
            fullWidth
            sx={{ mt: 0.5, mb: 2 }}
            InputLabelProps={{ shrink: true }}
            error={!!bfFieldErrors.start_date}
            helperText={bfFieldErrors.start_date || t('data.theFirstDayToLoad')}
          />
          <TextField
            label={t('data.toDate')}
            type="date"
            value={bfEnd}
            onChange={(e) => setBfEnd(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            error={!!bfFieldErrors.end_date}
            helperText={bfFieldErrors.end_date || t('data.exclusiveDataLoadsUpToThisDay')}
          />
          <TextField
            label={t('data.daysPerChunk')}
            type="number"
            value={bfSliceDays}
            onChange={(e) => setBfSliceDays(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
            helperText={t('data.leaveBlankToUseTheSourceSetting')}
          />
          <TextField
            label={t('data.concurrentChunks')}
            type="number"
            value={bfParallelism}
            onChange={(e) => setBfParallelism(e.target.value)}
            fullWidth
            inputProps={{ min: 1 }}
            helperText={t('data.leaveBlankToUseTheSourceSetting')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowBackfill(false)} disabled={backfillPipeline.isPending} color="inherit">{t('data.cancel')}</Button>
          <Button
            onClick={handleBackfillSubmit}
            variant="contained"
            disabled={backfillPipeline.isPending}
            startIcon={backfillPipeline.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >{t('data.startBackfill')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const RUN_DOT = { success: 'positiveFill', failed: 'negativeFill', running: 'info', pending: 'iconMuted', cancelled: 'iconMuted' };

/**
 * One run: dot, when, how it was started, then rows — or, for a failure, one
 * phrase and the run's name to quote to support. The full log stays with
 * support (decided 14 Oct): it can carry infrastructure detail.
 */
function HistoryRow({ run, now, past, action }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  let main;
  if (run.status === 'failed') {
    main = (
      <>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: past ? themeTokens.inkMuted : themeTokens.ink }}>
          {failureOf(run).text}
        </Typography>
        <RunRef name={run.name} />
      </>
    );
  } else if (run.status === 'success') {
    main = (
      <Typography sx={{ fontSize: '0.85rem' }}>
        {run.row_count === 0 ? t('data.noNewData') : run.row_count != null ? rowsLabel(run.row_count) : t('data.succeeded')}
      </Typography>
    );
  } else {
    const word = { running: t('data.running'), pending: t('data.queued'), cancelled: t('data.cancelled') }[run.status] || run.status;
    main = <Typography sx={{ fontSize: '0.85rem', color: themeTokens.inkMuted }}>{word}</Typography>;
  }

  // A backfill slice says which day it reloaded instead of "догрузка истории".
  const how = run.backfill_job && run.slice_start
    ? t('data.backfillForValue', { value1: localeDate(new Date(run.slice_start), { day: 'numeric', month: 'short' }) })
    : triggerText(run);

  return (
    <Box
      sx={{
        display: 'grid',
        // Fixed columns: each row is its own grid, so an auto-sized one would
        // shift with its text and the rows would not line up.
        gridTemplateColumns: '10px 150px minmax(0,1fr) 64px',
        columnGap: 1.5,
        alignItems: 'start',
        py: 1,
        borderTop: `1px solid ${themeTokens.border}`,
        '&:first-of-type': { borderTop: 'none', pt: 0 },
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: '7px', bgcolor: themeTokens[RUN_DOT[run.status] || 'iconMuted'] }} />
      <Box>
        <Typography sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
          {whenLabel(runTime(run), now)}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: themeTokens.inkMuted }}>
          {how}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {main}
        {action && <Box sx={{ mt: 0.75 }}>{action}</Box>}
      </Box>
      <Typography sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {run.finished_at ? durationLabel(run.started_at || run.created_at, run.finished_at) : ''}
      </Typography>
    </Box>
  );
}
